import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@apollo/client";

import BdrSpinner from "../../components/BdrSpinner/BdrSpinner";
import Tracking from "../../components/TrackingComp";
import Auth from "../../utils/auth";
import { hasRole } from "../../utils/permissions";

import PublicV2Layout from "../layout/PublicV2Layout";
import { SEO_V2 } from "../theme/seoMetaV2";
import {
  QUERY_RECENT_BA_GROUPS,
  QUERY_MORE_BA_GROUPS,
} from "../../utils/GraphQL/queries";
import { baImageUrl } from "../../utils/baImageUrl";
import BAGroupTimeFormat from "../../utils/formatters/BAGroupTimeFormat";
import nameListFormat from "../../utils/formatters/refinisherFormat";
import "./ourDoorsV2.css";
import "../icons/serviceIcons.css";

import ServiceSanderIcon from "../icons/ServiceSanderIcon";
import ServiceSprayerIcon from "../icons/ServiceSprayerIcon";
import ServiceStarIcon from "../icons/ServiceStarIcon";
import DoorStyleBrowser from "../../components/DoorStyleBrowser/DoorStyleBrowser";

// nameListFormat throws on a null/empty list — guard before calling it.
const refinishedBy = (group) =>
  group?.refinishers?.length ? nameListFormat(group.refinishers) : null;

const INFO_CHIPS = [
  { icon: <ServiceSanderIcon />, label: "Expert Craftsmanship" },
  { icon: <ServiceSprayerIcon />, label: "Premium Finishes" },
  { icon: <ServiceStarIcon />, label: "Real Results" },
];

function DoorCard({ group, onOpen }) {
  const afterUrl =
    group?.afterId?.s3ThumbUrl ??
    baImageUrl(group?.afterId?._id, { variant: "thumb" });
  const beforeUrl =
    group?.beforeId?.s3ThumbUrl ??
    baImageUrl(group?.beforeId?._id, { variant: "thumb" });

  return (
    <div className="bdrv2-door-card bdrv2-glass">
      <button
        type="button"
        className="bdrv2-door-pair"
        onClick={() => onOpen(group)}
        aria-label="Enlarge before and after"
      >
        <span className="bdrv2-door-half">
          {beforeUrl ? (
            <img src={beforeUrl} alt="Before refinishing" loading="lazy" decoding="async" />
          ) : (
            <span className="bdrv2-door-img-empty" aria-hidden="true" />
          )}
          <span className="bdrv2-door-side-tag bdrv2-door-tag-before">BEFORE</span>
        </span>
        <span className="bdrv2-door-half">
          {afterUrl ? (
            <img src={afterUrl} alt="After refinishing" loading="lazy" decoding="async" />
          ) : (
            <span className="bdrv2-door-img-empty" aria-hidden="true" />
          )}
          <span className="bdrv2-door-side-tag">AFTER</span>
        </span>
      </button>

      <div className="bdrv2-door-meta">
        <span className="bdrv2-door-stain">{group?.stainColor || "Custom Stain"}</span>
        <span className="bdrv2-door-date">
          {BAGroupTimeFormat(group?.dateEntered, false, true, true)}
        </span>
        {refinishedBy(group) && (
          <span className="bdrv2-door-refinisher">
            Refinished by <span className="bdrv2-accent-text">{refinishedBy(group)}</span>
          </span>
        )}
      </div>
    </div>
  );
}

function Lightbox({ group, onClose }) {
  const beforeUrl = group?.beforeId?.s3ImageUrl ?? baImageUrl(group?.beforeId?._id);
  const afterUrl = group?.afterId?.s3ImageUrl ?? baImageUrl(group?.afterId?._id);
  const slides = [
    { url: beforeUrl, label: "Before" },
    { url: afterUrl, label: "After" },
  ].filter((s) => s.url);

  // Mobile carousel: track which slide is centered so the dots can reflect it.
  const imgsRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const onImgsScroll = (e) => {
    const el = e.currentTarget;
    if (el.clientWidth) setActiveIdx(Math.round(el.scrollLeft / el.clientWidth));
  };
  const goToSlide = (i) => {
    const el = imgsRef.current;
    if (el) el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
  };

  // Preload the full-size before/after images while the darkened backdrop +
  // spinner show, so the modal never opens empty. Only flip `loaded` (which
  // reveals the inner with its open animation) once both are in the browser
  // cache — the <img> tags below then paint instantly. Reset on group change,
  // and don't hang forever if an image 404s (onerror also counts as done).
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!group) return undefined;
    setLoaded(false);
    const urls = [beforeUrl, afterUrl].filter(Boolean);
    if (!urls.length) {
      setLoaded(true);
      return undefined;
    }
    let cancelled = false;
    let remaining = urls.length;
    const settle = () => {
      if (cancelled) return;
      remaining -= 1;
      if (remaining <= 0) setLoaded(true);
    };
    const imgs = urls.map((u) => {
      const img = new Image();
      img.onload = settle;
      img.onerror = settle;
      img.src = u;
      return img;
    });
    return () => {
      cancelled = true;
      imgs.forEach((img) => {
        img.onload = null;
        img.onerror = null;
      });
    };
  }, [group, beforeUrl, afterUrl]);

  if (!group) return null;

  return (
    <div className="bdrv2-lightbox" onClick={onClose} role="dialog" aria-modal="true">
      {!loaded ? (
        <div className="bdrv2-lightbox-loading" onClick={(e) => e.stopPropagation()}>
          <BdrSpinner message="Loading..." />
        </div>
      ) : (
      <div className="bdrv2-lightbox-inner" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="bdrv2-lightbox-close" onClick={onClose} aria-label="Close">
          &times;
        </button>
        <div className="bdrv2-lightbox-imgs" ref={imgsRef} onScroll={onImgsScroll}>
          {slides.map((s) => (
            <figure key={s.label}>
              <img src={s.url} alt={s.label} />
              <figcaption>{s.label}</figcaption>
            </figure>
          ))}
        </div>
        {/* swipe dots — only rendered/visible on the mobile carousel (CSS) */}
        {slides.length > 1 && (
          <div className="bdrv2-lightbox-dots">
            {slides.map((s, i) => (
              <button
                key={s.label}
                type="button"
                className={`bdrv2-lightbox-dot${i === activeIdx ? " active" : ""}`}
                onClick={() => goToSlide(i)}
                aria-label={`View ${s.label}`}
              />
            ))}
          </div>
        )}
        <div className="bdrv2-lightbox-meta">
          <span className="bdrv2-accent-text">{group?.stainColor || "Custom Stain"}</span>
        </div>
        <div className="bdrv2-lightbox-bottom">
          {refinishedBy(group) && (
            <p className="bdrv2-lightbox-refinisher">
              Refinished by{" "}
              <span className="bdrv2-accent-text">{refinishedBy(group)}</span>
            </p>
          )}
          <span className="bdrv2-lightbox-date">
            {BAGroupTimeFormat(group?.dateEntered, false, true, true)}
          </span>
        </div>
      </div>
      )}
    </div>
  );
}

const OurDoorsV2 = (navProps) => {
  // Stable across renders — an inline Date.now() changed the query variables
  // every render and caused useQuery to refetch in an infinite loop.
  const local = useMemo(() => Date.now().toString(), []);
  const { data: recentData } = useQuery(QUERY_RECENT_BA_GROUPS, {
    variables: { localTimeEpoch: local, page: "1" },
    fetchPolicy: "cache-first",
  });
  const { data: moreData } = useQuery(QUERY_MORE_BA_GROUPS, {
    variables: { localTimeEpoch: local },
    fetchPolicy: "cache-first",
  });

  const recents = (recentData?.getRecentBAGs?.BAGData || []).slice(0, 8);
  const more = moreData?.getMoreDoors || [];
  const [active, setActive] = useState(null);

  // "Doors Like Mine" browse/upload tool — HARDCODED OFF entirely (2026-07-22,
  // per Taylor: not supposed to be exposed at all for now, including to the
  // developer role). Mirrors the V1 page's `false &&` pattern; the trailing
  // hasRole(...) keeps the imports referenced so CI's no-unused-vars stays
  // happy. Remove the `false &&` to restore the developer-only gate.
  const showDoorTool = false && hasRole(Auth.getPermissions(), "developer");

  return (
    <PublicV2Layout seo={SEO_V2.ourDoors} {...navProps}>
      <Tracking page={"ourDoors"} data-nosnippet />
      {/* HEADER */}
      <section className="bdrv2-section bdrv2-doors-head">
        <div className="bdrv2-container">
          <div className="bdrv2-doors-head-grid">
            <div>
              <p className="bdrv2-eyebrow bdrv2-accent-text">
                Beautiful Doors · Expert Refinishing
              </p>
              <h1 className="bdrv2-doors-h1">Our Doors</h1>
              <p className="bdrv2-doors-lead">
                Every door tells a story. See how we refinish weathered wood doors
                and bring them back to life across San Antonio, the Texas Hill
                Country, and South Texas.
              </p>
            </div>
            <div className="bdrv2-doors-info bdrv2-glass">
              <h3>We don&apos;t just refinish doors.</h3>
              <p>We bring back their character and color, and protect them from the Texas sun.</p>
              <div className="bdrv2-doors-chips">
                {INFO_CHIPS.map((c) => (
                  <span key={c.label} className="bdrv2-doors-chip">
                    <span className="bdrv2-accent-text">{c.icon}</span>
                    {c.label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DOORS LIKE MINE — door-gallery classification tool (browse-by-style +
          upload match). Still in development: gated to the developer role so it
          ships dark to the public. Gate the whole <section> (not just the
          component) so non-developers get no empty padded gap. */}
      {showDoorTool && (
        <section className="bdrv2-section bdrv2-doors-sec">
          <div className="bdrv2-container">
            <DoorStyleBrowser />
          </div>
        </section>
      )}

      {/* RECENTS */}
      <section className="bdrv2-section bdrv2-doors-sec">
        <div className="bdrv2-container">
          <h2 className="bdrv2-doors-section-title">See Our Latest Transformations</h2>
          {recents.length ? (
            <div className="bdrv2-door-grid">
              {recents.map((g, i) => (
                <DoorCard key={(g?._id || "r") + i} group={g} onOpen={setActive} />
              ))}
            </div>
          ) : (
            <p className="bdrv2-doors-empty">Loading recent projects…</p>
          )}
        </div>
      </section>

      {/* MORE */}
      <section className="bdrv2-section bdrv2-doors-sec">
        <div className="bdrv2-container">
          <h2 className="bdrv2-doors-section-title">Explore More Transformations</h2>
          {more.length ? (
            <div className="bdrv2-door-grid">
              {more.map((g, i) => (
                <DoorCard key={(g?._id || "m") + i} group={g} onOpen={setActive} />
              ))}
            </div>
          ) : (
            <p className="bdrv2-doors-empty">Loading more doors…</p>
          )}
        </div>
      </section>

      {/* key on the active id so each open remounts fresh (loaded=false) →
          the spinner shows first; without this, a reopen renders the previous
          loaded=true state and flashes the new (uncached) pair before the
          preload gate kicks in */}
      <Lightbox key={active?._id || "closed"} group={active} onClose={() => setActive(null)} />
    </PublicV2Layout>
  );
};

export default OurDoorsV2;
